import { Component, computed, inject, input } from '@angular/core';
import { ThemeMode, ThemeService } from '../../services/theme.service';

export type LoadingTextWord = 'loading' | 'updating' | 'saving' | 'removing' | 'connecting';
export type LoadingTextColor = 'auto' | 'white' | 'purple' | 'deep-purple';
export type LoadingTextSize = 22 | 24 | 30;

/** Readable labels for each animated word (image alt text and accessible status names). */
export const WORD_LABELS: Record<LoadingTextWord, string> = {
  loading: 'Loading',
  updating: 'Updating',
  saving: 'Saving',
  removing: 'Removing',
  connecting: 'Connecting',
};

/**
 * Animated "loading text" graphic: the word with a vertical purple shine, followed by three sliding dots.
 * Artwork is an animated SVG served from assets/loaders/text. 'auto' color follows the theme
 * (white in dark mode, deep purple in light mode).
 * FR/Task ID: Feature 2643 / Task 2862 (spinners & loading icons)
 */
@Component({
  selector: 'app-loading-text',
  standalone: true,
  template: `<img class="loading-text__img" [src]="src()" [alt]="alt()" />`,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .loading-text__img {
        display: block;
        flex-shrink: 0;
      }
    `,
  ],
  host: {
    role: 'status',
  },
})
export class LoadingTextComponent {
  private readonly themeService = inject(ThemeService);

  /** Which word to show. */
  readonly text = input<LoadingTextWord>('loading');

  /** Text color; 'auto' follows the active theme. */
  readonly color = input<LoadingTextColor>('auto');

  /** Theme the 'auto' color resolves against; 'auto' follows the app theme. */
  readonly mode = input<ThemeMode>('auto');

  /** Font size in pixels of the artwork (22, 24 or 30). */
  readonly size = input<LoadingTextSize>(24);

  protected readonly src = computed(
    () => `assets/loaders/text/${this.text()}-${this.resolvedColor()}-${this.size()}.svg`,
  );

  protected readonly alt = computed(() => WORD_LABELS[this.text()]);

  private readonly resolvedColor = computed(() => {
    const color = this.color();
    if (color !== 'auto') {
      return color;
    }
    const theme = this.mode() === 'auto' ? this.themeService.current() : this.mode();
    return theme === 'light' ? 'deep-purple' : 'white';
  });
}
